import { useDraggable } from '@dnd-kit/core'
import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type MouseEvent } from 'react'
import { useIntl } from 'react-intl'
import styled, { keyframes } from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'

import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import TextButton from 'LuxKit/Button/TextButton'
import IconButton from 'LuxKit/Button/IconButton'
import ResponsiveDropdownList from 'tripPlanner/components/Common/Dropdowns/ResponsiveDropdownList'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import AISparkleIcon from 'components/Search/SearchForm/AISparkleIcon'
import LineEllipsisVIcon from 'LuxKit/Icons/line/LineEllipsisVIcon'
import LineArrowsSwapVerticalIcon from 'LuxKit/Icons/line/LineArrowsSwapVerticalIcon'
import LinePenIcon from 'LuxKit/Icons/line/LinePenIcon'
import LineSpinnerIcon from 'LuxKit/Icons/line/LineSpinnerIcon'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import type { TripStop } from 'tripPlanner/api/types'
import type { TripListRow, TripListStatusValue } from './tripListTypes'
import TripListRowPrice from './TripListRowPrice'
import { TRIP_LIST_GRID_COLUMNS } from './tripListLayout'
import TripListTypeIcon from './TripListTypeIcon'
import TripListCheckboxButton from './TripListCheckboxButton'
import TripListRowNote from './TripListRowNote'
import TripListRowStatus from './TripListRowStatus'
import TripListMoveDayMenuItem from './TripListMoveDayMenuItem'
import { canAskAiToMoveTripListRow, canMoveTripListRowToAnotherDay, getTripListMoveDayOptions, type TripListMoveDayOption } from './tripListMoveDayOptions'
import { getDragOverlayDraggableId } from '../tripDragReorder'

const Cell = styled.div`
  align-self: stretch;
  display: flex;
  align-items: center;
  min-width: 0;
  padding: ${rem(9)} ${rem(6)};
  background: transparent;

  &.is-metadata-cell {
    background: var(--trip-list-row-metadata-background, transparent);
  }
`

const LeftAlignedCell = styled(Cell)`
  justify-content: flex-start;
  text-align: start;
`

const Row = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: ${TRIP_LIST_GRID_COLUMNS};
  grid-template-areas: "select meta title status price action";
  min-height: ${rem(46)};
  --trip-list-row-background: ${props => props.theme.palette.neutral.surface.normalBackground};
  --trip-list-row-metadata-background: transparent;
  background: var(--trip-list-row-background);
  transition: box-shadow 120ms ease, background 120ms ease;

  & + & {
    .is-row-content-cell {
      border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
    }
  }

  &.is-clickable {
    cursor: pointer;
  }

  &.is-draggable {
    cursor: grab;
  }

  &.is-dragging {
    z-index: 3;

    .is-row-content-cell {
      opacity: 0.24;
    }

    &::after {
      content: "";
      position: absolute;
      top: ${rem(6)};
      inset-inline-end: ${rem(6)};
      bottom: ${rem(6)};
      inset-inline-start: calc(${rem(40)} + ${rem(128)} + ${rem(6)});
      border: 1px dashed ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
      border-radius: ${props => props.theme.borderRadius.S};
      pointer-events: none;
    }
  }

  &.is-drop-target-before,
  &.is-drop-target-after {
    z-index: 2;
  }

  &.is-drop-target-before::before,
  &.is-drop-target-after::before {
    content: "";
    position: absolute;
    inset-inline-start: 0;
    inset-inline-end: 0;
    height: ${rem(3)};
    border-radius: ${props => props.theme.borderRadius.round};
    background: ${props => props.theme.palette.neutral.surface.normalContrast};
    pointer-events: none;
  }

  &.is-drop-target-before::before {
    top: 0;
  }

  &.is-drop-target-after::before {
    bottom: 0;
  }

  /* An "after" indicator sits directly above the next row's 1px content border,
     which reads as a thicker/doubled line. Drop that border so the 3px indicator
     matches the "before" case exactly. */
  &.is-drop-target-after + & .is-row-content-cell {
    border-top-color: transparent;
  }

  &.is-clickable:hover {
    z-index: 1;
    --trip-list-row-background: ${props => props.theme.palette.neutral.surface.lightBackground};
    box-shadow: 0 ${rem(4)} ${rem(12)} rgba(0, 0, 0, 0.12);

    .is-row-content-cell {
      border-top-color: transparent;
    }
  }

  &.is-dragging,
  &.is-dragging:hover {
    --trip-list-row-background: ${props => props.theme.palette.neutral.surface.normalBackground};
    box-shadow: none;
  }

  &.is-selected,
  &.is-selected:hover {
    --trip-list-row-background: ${props => `color-mix(in srgb, ${props.theme.palette.highlight.primary.lightBackground}, ${props.theme.palette.neutral.surface.normalBackground} 50%)`};
    --trip-list-row-metadata-background: var(--trip-list-row-background);
  }

  &.is-first-row {
    border-start-start-radius: ${props => props.theme.borderRadius.L};
    border-start-end-radius: ${props => props.theme.borderRadius.L};
  }

  &.is-last-row {
    border-end-end-radius: ${props => props.theme.borderRadius.L};
    border-end-start-radius: ${props => props.theme.borderRadius.L};
  }

  &.is-drag-overlay {
    --trip-list-row-background: ${props => props.theme.palette.neutral.surface.normalBackground};
    box-shadow: 0 ${rem(4)} ${rem(12)} rgba(0, 0, 0, 0.12);

    .is-row-content-cell {
      background: ${props => props.theme.palette.neutral.surface.normalBackground};
    }

    /* Only drag the content card, not the day/checkbox metadata column or its
       vertical divider line. */
    .is-metadata-cell {
      visibility: hidden;
    }
  }
`

const RowCheckboxSlot = styled.div`
  opacity: 0;
  transition: opacity 120ms ease;
  cursor: pointer;

  input {
    cursor: pointer;
  }

  ${Row}:hover &,
  &.is-selected {
    opacity: 1;
  }
`

const CheckboxCell = styled(Cell)`
  grid-area: select;
  align-items: flex-start;
  justify-content: center;
  padding-top: ${rem(16)};
  border-inline-end: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  cursor: pointer;
`

const MetadataCell = styled(Cell)`
  grid-area: meta;
  align-items: flex-start;
  padding-top: ${rem(16)};
`

const TitleCell = styled(Cell)`
  grid-area: title;
`

const StatusCell = styled(LeftAlignedCell)`
  grid-area: status;
`

const PriceCell = styled(LeftAlignedCell)`
  grid-area: price;
`

const ActionCell = styled(Cell)`
  grid-area: action;
  justify-content: flex-end;
`

const MetadataText = styled(BodyText)`
  overflow-wrap: anywhere;
`

const MetadataStack = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: ${rem(2)};
`

const TitleContent = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: ${rem(2)};
`

const CompactBookButton = styled(TextButton)`
  min-width: ${rem(56)};
  padding-inline: ${rem(12)};
  border-radius: ${props => props.theme.borderRadius.round};
`

const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${rem(2)};
  min-width: 0;
  width: 100%;
`

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

const SpinnerIcon = styled(LineSpinnerIcon)`
  animation: ${spin} 800ms linear infinite;
`

type ActionMenuView = 'actions' | 'moveDay'

interface Props {
  row: TripListRow
  tripStops: Array<TripStop>
  selected: boolean
  showGroupMetadata: boolean
  showLocationMetadata: boolean
  firstRow: boolean
  lastRow: boolean
  luxPlusEnabled: boolean
  onSelectRow: (rowId: string, checked: boolean) => void
  onRowClick: (row: TripListRow) => void
  onBookRow: (row: TripListRow) => void
  onViewBooking: (row: TripListRow) => void
  onAskAi: (row: TripListRow, origin?: { x: number; y: number }) => void
  onAskAiMove: (row: TripListRow, origin?: { x: number; y: number }) => void
  onDelete: (row: TripListRow) => Promise<void>
  onMoveToDay: (row: TripListRow, option: TripListMoveDayOption) => void | boolean | Promise<void | boolean>
  onUpdateNote: (row: TripListRow, notes: string | undefined) => void
  onUpdateStatus: (row: TripListRow, status: TripListStatusValue) => void
  isBookingPending?: boolean
  isNoteUpdatePending?: boolean
  isStatusUpdatePending?: boolean
  notesDisabled?: boolean
  statusDisabled?: boolean
  actionsDisabled?: boolean
  dragDisabled?: boolean
  isDragOverlay?: boolean
  dropTargetPlacement?: 'before' | 'after'
}

function TripListRowItem({
  row,
  tripStops,
  selected,
  showGroupMetadata,
  showLocationMetadata,
  firstRow,
  lastRow,
  luxPlusEnabled,
  onSelectRow,
  onRowClick,
  onBookRow,
  onViewBooking,
  onAskAi,
  onAskAiMove,
  onDelete,
  onMoveToDay,
  onUpdateNote,
  onUpdateStatus,
  isBookingPending = false,
  isNoteUpdatePending = false,
  isStatusUpdatePending = false,
  notesDisabled = false,
  statusDisabled = false,
  actionsDisabled = false,
  dragDisabled: dragDisabledProp = false,
  isDragOverlay = false,
  dropTargetPlacement,
}: Props) {
  const intl = useIntl()
  const [menuOpen, setMenuOpen] = useState(false)
  const [menuView, setMenuView] = useState<ActionMenuView>('actions')
  const [noteEditing, setNoteEditing] = useState(false)
  const menuTriggerRef = useRef<HTMLButtonElement>(null)
  const canMoveToAnotherDay = canMoveTripListRowToAnotherDay(row)
  const canAskAiToMove = canAskAiToMoveTripListRow(row)
  const dragDisabled = dragDisabledProp || actionsDisabled || row.isBooked || isBookingPending || isNoteUpdatePending || isStatusUpdatePending || !canMoveToAnotherDay
  const hasViewAction = row.viewTarget.kind !== 'none'
  const moveDayOptions = useMemo(() => getTripListMoveDayOptions(row, tripStops, intl), [intl, row, tripStops])
  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging,
  } = useDraggable({
    // The overlay copy must not share the real row's id - see getDragOverlayDraggableId.
    id: isDragOverlay ? getDragOverlayDraggableId(row.id) : row.id,
    disabled: dragDisabled,
  })

  useEffect(() => {
    if (actionsDisabled) {
      setMenuOpen(false)
      setMenuView('actions')
    }
  }, [actionsDisabled])

  const handleRowClick = useCallback(() => {
    if (!hasViewAction) return
    onRowClick(row)
  }, [hasViewAction, onRowClick, row])

  const handleControlClick = useCallback((event: MouseEvent) => {
    event.stopPropagation()
  }, [])

  const handleCheckboxChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    onSelectRow(row.id, event.currentTarget.checked)
  }, [onSelectRow, row.id])

  const handleCheckboxCellClick = useCallback((event: MouseEvent) => {
    event.stopPropagation()
    onSelectRow(row.id, !selected)
  }, [onSelectRow, row.id, selected])

  const toggleMenu = useCallback((event: MouseEvent) => {
    event.stopPropagation()
    if (actionsDisabled) return
    setMenuOpen(prev => {
      const nextOpen = !prev
      if (nextOpen) setMenuView('actions')
      return nextOpen
    })
  }, [actionsDisabled])

  const closeMenu = useCallback(() => {
    setMenuOpen(false)
    setMenuView('actions')
  }, [])

  // The "..." trigger stays mounted after the menu closes, so it's a stable
  // launch point for the Ask AI context comet (see AiContextTrace).
  const getAskAiOrigin = useCallback((): { x: number; y: number } | undefined => {
    const rect = menuTriggerRef.current?.getBoundingClientRect()
    if (!rect) return undefined
    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
  }, [])

  const handleAskAi = useCallback(() => {
    if (actionsDisabled) return
    const origin = getAskAiOrigin()
    closeMenu()
    onAskAi(row, origin)
  }, [actionsDisabled, closeMenu, getAskAiOrigin, onAskAi, row])

  const handleAskAiMove = useCallback(() => {
    if (actionsDisabled || !canAskAiToMove) return
    const origin = getAskAiOrigin()
    closeMenu()
    onAskAiMove(row, origin)
  }, [actionsDisabled, canAskAiToMove, closeMenu, getAskAiOrigin, onAskAiMove, row])

  const handleDelete = useCallback(() => {
    if (actionsDisabled) return
    closeMenu()
    onDelete(row)
  }, [actionsDisabled, closeMenu, onDelete, row])

  const handleEditNote = useCallback(() => {
    if (actionsDisabled || notesDisabled) return
    closeMenu()
    setNoteEditing(true)
  }, [actionsDisabled, closeMenu, notesDisabled])

  const handleMoveDayMenuOpen = useCallback(() => {
    if (actionsDisabled || !canMoveToAnotherDay) return
    setMenuView('moveDay')
  }, [actionsDisabled, canMoveToAnotherDay])

  const handleMoveDayOptionClick = useCallback((option: TripListMoveDayOption) => {
    closeMenu()
    if (actionsDisabled || option.selected) return
    onMoveToDay(row, option)
  }, [actionsDisabled, closeMenu, onMoveToDay, row])

  const handleNoteChange = useCallback((notes: string | undefined) => {
    if (notesDisabled || isNoteUpdatePending) return
    onUpdateNote(row, notes)
  }, [isNoteUpdatePending, notesDisabled, onUpdateNote, row])

  const handleStatusChange = useCallback((status: TripListStatusValue) => {
    if (statusDisabled || isStatusUpdatePending) return
    onUpdateStatus(row, status)
  }, [isStatusUpdatePending, onUpdateStatus, row, statusDisabled])

  const handleCtaClick = useCallback((event: MouseEvent) => {
    event.stopPropagation()
    if (actionsDisabled || isBookingPending) return
    onBookRow(row)
  }, [actionsDisabled, isBookingPending, onBookRow, row])

  const handleViewBookingClick = useCallback((event: MouseEvent) => {
    event.stopPropagation()
    if (actionsDisabled) return
    onViewBooking(row)
  }, [actionsDisabled, onViewBooking, row])

  const handleViewRowClick = useCallback((event: MouseEvent) => {
    event.stopPropagation()
    if (actionsDisabled || !hasViewAction) return
    onRowClick(row)
  }, [actionsDisabled, hasViewAction, onRowClick, row])
  const bookLabel = intl.formatMessage({
    id: 'tumE0yl0JumvbIhBIWAD',
    defaultMessage: 'Book',
    description: 'CTA button label for bookable rows in the trip planner list view',
  })

  const viewBookingLabel = intl.formatMessage({
    id: 'XJ481es7biCMHYe_wDuK',
    defaultMessage: 'Manage',
    description: 'CTA button label for managing booked rows in the trip planner list view',
  })

  const viewLabel = intl.formatMessage({
    id: '9V1THFwaUjdBLk4x3kKO',
    defaultMessage: 'View',
    description: 'CTA button label for viewing rows in the trip planner list view',
  })

  const searchLabel = intl.formatMessage({
    id: '2GVNYIaOFwkpoPHJWkJ2',
    defaultMessage: 'Search',
    description: 'CTA button label for searching bookable flights from a custom flight row in the trip planner list view',
  })

  const isSearchAction = row.viewTarget.kind === 'flightSearch' && row.rowType === 'flight'
  const rowTripItemId = row.kind === 'item' ? row.item.id : row.option.itemId
  const moveToAnotherDayLabel = intl.formatMessage({
    id: 'HapGLjj4DK7cTzBFH3yJ',
    defaultMessage: 'Move to another day',
    description: 'Dropdown menu item for opening day move options in the trip planner list row',
  })

  const moveWithAiLabel = intl.formatMessage({
    id: 'OefXR3E6hTASV2s-dov6',
    defaultMessage: 'Move with AI',
    description: 'Dropdown menu item for using AI to move a complex trip planner list row',
  })

  const addNoteLabel = intl.formatMessage({
    id: 'cnVq7DmC07wNFmbnUgrs',
    defaultMessage: 'Add note',
    description: 'Placeholder text for empty notes cells in the TripChat itinerary list view',
  })

  const editNoteLabel = intl.formatMessage({
    id: 'KkHMzER0vMwbWcAq8trX',
    defaultMessage: 'Edit note',
    description: 'Dropdown menu item for editing a trip planner list row note',
  })

  const noteActionLabel = row.note ? editNoteLabel : addNoteLabel

  const actionsMenuLabel = intl.formatMessage(
    {
      id: 'lwXy0ESWfgPHAcbNIY3l',
      defaultMessage: 'More actions for {title}',
      description: 'Accessible label for the trip planner list row actions menu button',
    },
    { title: row.title },
  )

  return (
    <Row
      onClick={hasViewAction ? handleRowClick : undefined}
      ref={setNodeRef}
      data-trip-list-row-id={row.id}
      data-stop-id={row.stop.id}
      data-day-id={row.day?.id}
      data-item-id={rowTripItemId}
      data-trip-list-drag-disabled={dragDisabled ? 'true' : undefined}
      className={clsx(
        selected && 'is-selected',
        hasViewAction && 'is-clickable',
        !dragDisabled && 'is-draggable',
        isDragging && 'is-dragging',
        isDragOverlay && 'is-drag-overlay',
        dropTargetPlacement === 'before' && 'is-drop-target-before',
        dropTargetPlacement === 'after' && 'is-drop-target-after',
        firstRow && 'is-first-row',
        lastRow && 'is-last-row',
      )}
      {...(!dragDisabled ? attributes : undefined)}
      {...(!dragDisabled ? listeners : undefined)}
    >
      <CheckboxCell className="is-metadata-cell" onClick={handleCheckboxCellClick}>
        <RowCheckboxSlot className={selected ? 'is-selected' : undefined} onClick={handleControlClick}>
          <TripListCheckboxButton
            size="small"
            className="is-table-checkbox"
            checked={selected}
            onChange={handleCheckboxChange}
            aria-label={intl.formatMessage(
              {
                id: 'qevuYZDilBy9necmVigz',
                defaultMessage: 'Select {title}',
                description: 'Accessible label for selecting a row in the trip planner list view',
              },
              { title: row.title },
            )}
          />
        </RowCheckboxSlot>
      </CheckboxCell>
      <MetadataCell className="is-metadata-cell">
        {showGroupMetadata && (
          <MetadataStack>
            <MetadataText variant="medium" colour="normal-contrast">{row.dayLabel}</MetadataText>
            {showLocationMetadata && (
              <MetadataText variant="medium" colour="light-foreground-alt" lineClamp={3}>
                {row.location.label}
              </MetadataText>
            )}
          </MetadataStack>
        )}
      </MetadataCell>
      <TitleCell className="is-row-content-cell">
        <Group direction="horizontal" gap={12} verticalAlign="center" fullWidth>
          <TripListTypeIcon type={row.rowType} row={row} />
          <TitleContent>
            <BodyText variant="medium" weight="bold" lineClamp={1}>{row.title}</BodyText>
            {row.subtitle && (
              <Caption variant="large" colour="light-foreground-alt" lineClamp={2}>
                {row.subtitle}
              </Caption>
            )}
            <TripListRowNote
              note={row.note}
              title={row.title}
              disabled={notesDisabled}
              saving={isNoteUpdatePending}
              editing={noteEditing}
              onEditingChange={setNoteEditing}
              onChange={handleNoteChange}
            />
          </TitleContent>
        </Group>
      </TitleCell>
      <StatusCell className="is-row-content-cell is-status-cell">
        <TripListRowStatus
          status={row.status}
          title={row.title}
          disabled={statusDisabled}
          saving={isStatusUpdatePending}
          onChange={handleStatusChange}
        />
      </StatusCell>
      <PriceCell className="is-row-content-cell is-price-cell" onClick={handleControlClick}>
        <TripListRowPrice price={row.price} luxPlusEnabled={luxPlusEnabled} />
      </PriceCell>
      <ActionCell className="is-row-content-cell is-action-cell" onClick={handleControlClick}>
        <ActionGroup>
          {row.isBooked && (
            <CompactBookButton size="small" kind="secondary" onClick={handleViewBookingClick} disabled={actionsDisabled}>
              {viewBookingLabel}
            </CompactBookButton>
          )}
          {!row.isBooked && row.isManuallyBooked && hasViewAction && (
            <CompactBookButton size="small" kind="secondary" onClick={handleViewRowClick} disabled={actionsDisabled}>
              {isSearchAction ? searchLabel : viewLabel}
            </CompactBookButton>
          )}
          {!row.isBooked && !row.isManuallyBooked && row.isBookable && (
            <CompactBookButton
              size="small"
              kind="primary"
              onClick={handleCtaClick}
              disabled={actionsDisabled || isBookingPending}
              aria-busy={isBookingPending}
              startIcon={isBookingPending ? <SpinnerIcon size="XS" /> : false}
            >
              {bookLabel}
            </CompactBookButton>
          )}
          {!row.isBooked && !row.isManuallyBooked && !row.isBookable && hasViewAction && (
            <CompactBookButton size="small" kind="secondary" onClick={handleViewRowClick} disabled={actionsDisabled}>
              {isSearchAction ? searchLabel : viewLabel}
            </CompactBookButton>
          )}
          <IconButton
            size="small"
            kind="tertiary"
            onClick={toggleMenu}
            ref={menuTriggerRef}
            disabled={actionsDisabled}
            aria-label={actionsMenuLabel}
          >
            <LineEllipsisVIcon />
          </IconButton>
          <ResponsiveDropdownList
            size="S"
            open={menuOpen}
            onClose={closeMenu}
            triggerRef={menuTriggerRef}
            anchorRef={menuTriggerRef}
            placement="bottom-end"
            portaled
            width="min"
            enableFlip
            autoHeight
            title={menuView === 'moveDay' ? moveToAnotherDayLabel : undefined}
          >
            {menuView === 'actions' && (
              <>
                <ClickableListItem
                  title={intl.formatMessage({
                    id: 'G352qlaFJTQXJzpg0Sg2',
                    defaultMessage: 'Ask AI',
                    description: 'Dropdown menu item for asking AI about a trip planner list row',
                  })}
                  size="medium"
                  startIcon={<AISparkleIcon size="S" />}
                  onClick={handleAskAi}
                />
                {canMoveToAnotherDay && (
                  <ClickableListItem
                    title={moveToAnotherDayLabel}
                    size="medium"
                    startIcon={<LineArrowsSwapVerticalIcon size="S" />}
                    onClick={handleMoveDayMenuOpen}
                  />
                )}
                {canAskAiToMove && (
                  <ClickableListItem
                    title={moveWithAiLabel}
                    size="medium"
                    startIcon={<LineArrowsSwapVerticalIcon size="S" />}
                    onClick={handleAskAiMove}
                  />
                )}
                <ClickableListItem
                  title={noteActionLabel}
                  size="medium"
                  startIcon={<LinePenIcon size="S" />}
                  onClick={handleEditNote}
                />
                <ClickableListItem
                  title={intl.formatMessage({
                    id: 'NQx7k4Q98rLpfRiwQBQK',
                    defaultMessage: 'Delete',
                    description: 'Dropdown menu item for deleting a trip planner list row',
                  })}
                  size="medium"
                  startIcon={<LineTrashAltIcon size="S" />}
                  onClick={handleDelete}
                />
              </>
            )}
            {menuView === 'moveDay' && moveDayOptions.map(option => (
              <TripListMoveDayMenuItem
                key={option.id}
                option={option}
                onClick={handleMoveDayOptionClick}
              />
            ))}
          </ResponsiveDropdownList>
        </ActionGroup>
      </ActionCell>
    </Row>
  )
}

export default TripListRowItem
